package com.islamicmedia.shared.data.storage

import com.islamicmedia.shared.domain.model.DisambiguationItem
import com.islamicmedia.shared.domain.model.DisplaySettings
import com.islamicmedia.shared.domain.model.MediaPref
import com.islamicmedia.shared.domain.model.SearchCriteria
import com.islamicmedia.shared.presentation.HistoryItem

/**
 * The single in-flight analysis persisted so it can resume if the app is killed mid-run.
 * [quickCheck] is null for records saved before the mode was persisted; resume then falls back to
 * the current UI mode, which is what every build did before.
 */
data class PendingAnalysis(
    val title: String,
    val mediaType: String,
    val year: String?,
    val quickCheck: Boolean?,
)

expect class SettingsStorage() {
    /** Autocomplete catalog cache (served by /api/titles/index, matched locally for typeahead). */
    fun saveTitlesIndex(items: List<DisambiguationItem>)
    fun loadTitlesIndex(): List<DisambiguationItem>
    /** Recent search queries (most-recent first) for the empty-state suggestions. */
    fun saveRecentSearches(queries: List<String>)
    fun loadRecentSearches(): List<String>
    fun saveSearchCriteria(criteria: SearchCriteria)
    fun loadSearchCriteria(): SearchCriteria
    /** User "Display style" preference (analysis text size + color). */
    fun saveDisplaySettings(settings: DisplaySettings)
    fun loadDisplaySettings(): DisplaySettings
    fun saveHistory(history: List<HistoryItem>)
    fun loadHistory(): List<HistoryItem>
    /** Cached pin/hide prefs. Persisted so the Your Analyses list can be filtered on the FIRST
     *  frame — otherwise hidden titles flash in until the server fetch lands. */
    fun saveMediaPrefs(mediaPrefs: List<MediaPref>)
    fun loadMediaPrefs(): List<MediaPref>
    fun saveLastUserEmail(email: String)
    fun loadLastUserEmail(): String?
    fun savePendingAnalysis(title: String, mediaType: String, year: String?, quickCheck: Boolean)
    fun loadPendingAnalysis(): PendingAnalysis?
    fun clearPendingAnalysis()
    /**
     * Wipe per-user sensitive local data (analysis history, last email, pending
     * analysis). Called when auth resolves to NotAuthenticated so a signed-out
     * or expired session can never surface another account's analyses.
     */
    fun clearUserData()
    /** Local fallback for the dhikr high score — used for anonymous users and as a cache for signed-in. */
    fun saveDhikrHighScore(score: Int)
    fun loadDhikrHighScore(): Int
    /** Tracks how many analyses have completed successfully on this device (mobile only; drives the
     *  review prompt, see ReviewPromptRule). Per-device, not cleared by clearUserData. */
    fun saveAnalysisCount(count: Int)
    fun loadAnalysisCount(): Int
    /** Legacy counter from the old 1st/5th/10th milestone rule. No longer used for any decision
     *  (see [saveLastReviewPromptAt]); kept so the old key stays readable. */
    fun saveReviewPromptCount(count: Int)
    fun loadReviewPromptCount(): Int
    /**
     * The completed-analysis count at which we last asked the OS to show its review card (see
     * ReviewPromptRule: a new request is due once the count is 5 higher). Null when never stored
     * — the caller then seeds it from the current count, so existing installs are not prompted
     * the moment they update. Per-device, not cleared by clearUserData.
     */
    fun saveLastReviewPromptAt(count: Int)
    fun loadLastReviewPromptAt(): Int?
    /** Whether the first-run coachmark tour (search/filters/quick-check) has been shown (per-device). */
    fun saveHasSeenCoachmarks(seen: Boolean)
    fun loadHasSeenCoachmarks(): Boolean
    /** Whether the post-login credits coachmark (toggle badges) has been shown (per-device). */
    fun saveHasSeenCreditsCoachmark(seen: Boolean)
    fun loadHasSeenCreditsCoachmark(): Boolean
    /** Whether the post-payment filters coachmark has been shown (per-device). */
    fun saveHasSeenFiltersCoachmark(seen: Boolean)
    fun loadHasSeenFiltersCoachmark(): Boolean
    /** Whether the result-screen coachmarks (flag + highlights) have been shown (per-device). */
    fun saveHasSeenResultCoachmarks(seen: Boolean)
    fun loadHasSeenResultCoachmarks(): Boolean
    /**
     * Dev-only backend override ("PROD"/"DEV"/null). Read at app startup into
     * [com.islamicmedia.shared.AppConfig.runtimeOverride] (honored only on dev builds) so a dev
     * build can point at the PROD backend without a rebuild. Mirrors the web switcher's
     * localStorage["env-override"]. Null = use the compile-time default.
     */
    fun saveEnvOverride(value: String?)
    fun loadEnvOverride(): String?
    /**
     * Stable anonymous id for funnel tracking ([com.islamicmedia.shared.platform.Funnel]). Minted
     * on first launch and never rotated — rotating it would split one person's journey across two
     * ids and inflate every funnel step. NOT cleared by [clearUserData]: it identifies the device,
     * not the account, and the pre-registration half of the funnel depends on it surviving sign-out.
     */
    fun saveAnonId(id: String)
    fun loadAnonId(): String?
    /**
     * First-touch acquisition attribution as a JSON blob
     * ([com.islamicmedia.shared.platform.Attribution]) — where this person originally came from.
     * Written once and never overwritten: last-touch would credit whatever link the user happened
     * to click most recently, which is usually one of our own emails to someone we already have.
     * Like [saveAnonId] it describes the device/browser rather than the account, so it is NOT
     * cleared by [clearUserData] — signing out does not change where someone came from.
     */
    fun saveAttribution(json: String)
    fun loadAttribution(): String?
    /**
     * Whether the Play Install Referrer has already been read for this install. Only Android has a
     * referrer to read, but the flag is declared here so the one-shot guard has the same shape on
     * every platform. Re-arms on a genuine reinstall (Android backup is off), which is correct —
     * Play reports the same referrer again and first-touch wins anyway.
     */
    fun saveInstallReferrerCaptured(captured: Boolean)
    fun loadInstallReferrerCaptured(): Boolean
    /**
     * A friend's referral code waiting to be attached to an account: captured from a /r/ link
     * (web: `?refcode=` at module init) BEFORE the person has signed up, and posted to
     * /api/referral/attach after they sign in (MediaCheckerViewModel.flushPendingReferralCode).
     * [atMs] is when it was captured; null [code] clears it.
     *
     * NOT cleared by [clearUserData], deliberately: the capture happens while signed OUT, and auth
     * resolving to NotAuthenticated on that very page load would otherwise wipe it before sign-up.
     * It is cleared instead once the server gives a final verdict on it, and expires on its own.
     * Stored as one "CODE|epochMs" string through [PendingReferralCode] on every platform.
     */
    fun savePendingReferralCode(code: String?, atMs: Long)
    /** The stored code, or null when there is none, it is malformed, or it is older than
     *  [PendingReferralCode.MAX_AGE_MS] (30 days). */
    fun loadPendingReferralCode(): String?

    /**
     * The member's own referral answer (code, link, last counts) as [ReferralInfoCache] encodes it,
     * so the "Free Gift" screen opens without a network wait. Null removes it. Cleared by
     * [clearUserData] (it belongs to the signed-in account).
     */
    fun saveReferralCache(value: String?)
    fun loadReferralCache(): String?

    /**
     * Has THIS device already shown the one-time "Did a friend send you a code?" popup
     * (MediaCheckerUiState.referralRedeemPromptEligible, ReferralCopy.REDEEM_PROMPT_*) to
     * [owner] (ReferralInfoCache.owner(email, environment))? A device can hold more than one
     * account over its lifetime, so this is keyed per owner rather than one flag — see
     * [com.islamicmedia.shared.domain.model.ReferralRedeemPromptSeen]. NOT cleared by
     * [clearUserData]: it is already scoped per account, so signing out must not un-see it for
     * the account that just left.
     */
    fun hasSeenReferralRedeemPrompt(owner: String): Boolean
    /** Marks [owner] as having seen the popup. Idempotent — call it the instant the popup is
     *  actually put on screen, before either button is tapped, so a kill mid-popup can never show
     *  it twice. */
    fun markReferralRedeemPromptSeen(owner: String)

    /**
     * Has THIS device already acknowledged (tapped OK on) the one-time "your referral was
     * blocked" notice (ReferralCopy.REFERRAL_BLOCKED, owner's report 2026-09-25) for [owner]
     * ([com.islamicmedia.shared.domain.model.ReferralInfoCache.owner]) at [blockedAt] (GET
     * /api/referral/me's referral_blocked_at)? Keyed on the PAIR, not just [owner]
     * ([com.islamicmedia.shared.domain.model.ReferralBlockedAck]): a later, different block is its
     * own notice. NOT cleared by [clearUserData]: it is already scoped per account+block, so
     * signing out must not un-acknowledge it for the account that just left.
     */
    fun hasAcknowledgedReferralBlocked(owner: String, blockedAt: String): Boolean
    /** Marks (owner, blockedAt) acknowledged. Call it when the notice's OK button is tapped. */
    fun markReferralBlockedAcknowledged(owner: String, blockedAt: String)
}

/**
 * The one encoding of the pending referral code, shared by every [SettingsStorage] actual so the
 * 30-day expiry and the validation can't differ between platforms.
 */
internal object PendingReferralCode {
    /** A link clicked more than 30 days before signing up no longer counts. */
    const val MAX_AGE_MS = 30L * 24 * 60 * 60 * 1000

    /** "ABCD2345|1727000000000", or null when [code] isn't a real code (nothing is stored then). */
    fun encode(code: String?, atMs: Long): String? =
        com.islamicmedia.shared.util.ReferralCodeFormat.normalize(code)?.let { "$it|$atMs" }

    /** The canonical code, or null when [raw] is absent, malformed, expired, or dated in the future
     *  by more than a day (a clock that jumped: better to drop it than keep it forever). */
    fun decode(raw: String?, nowMs: Long): String? {
        if (raw.isNullOrBlank()) return null
        val code = com.islamicmedia.shared.util.ReferralCodeFormat.normalize(raw.substringBefore('|')) ?: return null
        val at = raw.substringAfter('|', "").toLongOrNull() ?: return null
        val age = nowMs - at
        if (age > MAX_AGE_MS || age < -ONE_DAY_MS) return null
        return code
    }

    private const val ONE_DAY_MS = 24L * 60 * 60 * 1000
}
